import {baseComparator, sorted} from 'common/util/array';
import {renameEvent} from 'common/report/util/events';
import {renameProperty, renamePropertyValue} from 'common/report/util/properties';
import {PROPERTY_TYPES, SESSION_EVENTS} from 'common/report/constants';

export const EVENT_SORT_TYPE = {
  ALPHA: `alpha`,
  USAGE: `usage`,
};

export function processEventNames(eventNames, {sortType = EVENT_SORT_TYPE.ALPHA}) {
  let items = eventNames
    .filter((name) => name !== `$web_event`)
    .map((name) => ({label: renameEvent(name), name, custom: false}));
  if (sortType === EVENT_SORT_TYPE.ALPHA) {
    items = items.sort(baseComparator({transform: (item) => item.label.toLowerCase()}));
  }
  return items;
}

const IGNORED_PROPERTIES = new Set([`$transactions`, `mp_event_id`, `$mp_visual_journeys`]);

export function processProperties(properties, {dataGroupId = null} = {}) {
  const propertyTypes = Object.values(PROPERTY_TYPES);
  const mappedProperties = Object.entries(properties)
    .filter(([name, details]) => !IGNORED_PROPERTIES.has(name) && propertyTypes.includes(details.type))
    .map(([name, details]) => {
      const dgi = dataGroupId || details.data_group_id;
      return {
        name,
        ...(details.join_property_type ? {joinPropertyType: details.join_property_type} : {}),
        ...(details.dimension_data_group_id ? {dimensionDataGroupId: details.dimension_data_group_id} : {}),
        ...(dgi ? {dataGroupId: dgi} : {}),
        ...details,
        label: renameProperty(name, {dataGroupId: dgi}),
        ...(details.items
          ? {
              items: Object.values(details.items),
            }
          : {}),
      };
    });

  return mappedProperties.sort(
    baseComparator({
      transform: (item) => item.label.toLowerCase(),
    }),
  );
}

export function processPropertyValues(propertyValues, {propertyName}) {
  return sorted(propertyValues ?? [], {
    transform: (value) => renamePropertyValue(value, propertyName).toLowerCase(),
  });
}

/**
 * Generates the "event" param for some API endpoints which
 * can be a single event string or JSON encoded list of events
 * @param {string|string[]} value
 */
export function getEventParam(value) {
  let events = Array.isArray(value) ? value : [];

  if (!Array.isArray(value) && value !== null) {
    events = [value];
  }

  const [event, sessionEvent] = events.reduce(
    ([event, sessionEvent], value) => {
      if (SESSION_EVENTS[value]) {
        return [event, [...sessionEvent, SESSION_EVENTS[value].session_event]];
      }

      return [[...event, value], sessionEvent];
    },
    [[], []],
  );

  return {
    ...(event.length
      ? {
          event: JSON.stringify(event),
        }
      : {}),
    ...(sessionEvent.length
      ? {
          session_event: JSON.stringify(sessionEvent),
        }
      : {}),
  };
}
